import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SEO, SEO_CONFIG } from '@/components/ui/SEO';
import {
  ChevronDown,
  Sparkles,
  Bug,
  Zap,
  Shield,
  Palette,
  Terminal,
  GitBranch,
  Search,
  Rss,
  Bell,
  CheckCircle2,
  Clock,
  Rocket,
  TestTube2,
  Globe,
  Package,
  ArrowRight,
  Star,
  ChevronRight
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { ChangelogBackground } from '@/components/ui/ChangelogBackground';

type CategoryType = 'feature' | 'performance' | 'bugfix' | 'security' | 'testing';

interface ChangelogEntry {
  version: string;
  date: string;
  title: string;
  highlight?: string;
  isLatest?: boolean;
  categories: {
    name: string;
    type: CategoryType;
    icon: typeof Sparkles;
    gradient: string;
    items: string[];
  }[];
}

const categoryFilterTypes: (CategoryType | 'all')[] = ['all', 'feature', 'performance', 'bugfix', 'security', 'testing'];

const changelog: ChangelogEntry[] = [
  {
    version: '2.4',
    date: 'Dec 25, 2025',
    title: 'Unit & UAT Testing Suite',
    highlight: 'Built-in testing without extra tools',
    isLatest: true,
    categories: [
      {
        name: 'Testing',
        type: 'testing',
        icon: TestTube2,
        gradient: 'from-green-500 to-emerald-500',
        items: [
          'Integrated Unit Testing - Run tests directly in the editor',
          'User Acceptance Testing (UAT) framework built-in',
          'AI-powered test case generation',
          'Visual test coverage reports with heat maps',
          'One-click test execution with inline results',
          'Support for Jest, Vitest, Mocha, and pytest',
        ],
      },
      {
        name: 'AI Agent',
        type: 'feature',
        icon: Sparkles,
        gradient: 'from-purple-500 to-violet-500',
        items: [
          'Claude Opus 4.5 integration for advanced reasoning',
          'GPT-4.5 Turbo support with faster responses',
          'Gemini 2.5 Pro for multimodal code analysis',
          'Grok 3 for real-time coding assistance',
        ],
      },
      {
        name: 'Performance',
        type: 'performance',
        icon: Zap,
        gradient: 'from-amber-500 to-orange-500',
        items: [
          '40% faster test execution with parallel runners',
          'Reduced memory footprint for test suites',
          'Instant test feedback with smart caching',
        ],
      },
    ],
  },
  {
    version: '2.3',
    date: 'Dec 22, 2025',
    title: 'AI Agent Improvements & Stability',
    categories: [
      {
        name: 'AI Agent',
        type: 'feature',
        icon: Sparkles,
        gradient: 'from-purple-500 to-violet-500',
        items: [
          'Improved context understanding for larger codebases',
          'New multi-file refactoring capabilities',
          'Better handling of complex TypeScript types',
          'Added support for custom AI rules per project',
        ],
      },
      {
        name: 'Performance',
        type: 'performance',
        icon: Zap,
        gradient: 'from-amber-500 to-orange-500',
        items: [
          'Faster startup time (30% improvement)',
          'Reduced memory usage for large projects',
          'Optimized file indexing performance',
        ],
      },
      {
        name: 'Bug Fixes',
        type: 'bugfix',
        icon: Bug,
        gradient: 'from-red-500 to-rose-500',
        items: [
          'Fixed cursor jumping issue in markdown files',
          'Resolved theme switching delay',
          'Fixed Git integration sync issues',
        ],
      },
    ],
  },
  {
    version: '2.2',
    date: 'Dec 8, 2025',
    title: 'Smart Terminal Features',
    categories: [
      {
        name: 'Terminal',
        type: 'feature',
        icon: Terminal,
        gradient: 'from-purple-500 to-violet-500',
        items: [
          'AI-powered command suggestions',
          'Integrated error analysis with fix suggestions',
          'Split terminal support with drag & drop',
          'Improved shell detection on Windows',
        ],
      },
      {
        name: 'Git',
        type: 'feature',
        icon: GitBranch,
        gradient: 'from-orange-500 to-red-500',
        items: [
          'Smart commit message generation with AI',
          'Conflict resolution assistant',
          'Improved diff visualization with syntax highlighting',
        ],
      },
      {
        name: 'Security',
        type: 'security',
        icon: Shield,
        gradient: 'from-green-500 to-teal-500',
        items: [
          'Added SCIM support for Enterprise',
          'Improved SSO configuration wizard',
          'New audit log features with export',
        ],
      },
    ],
  },
  {
    version: '2.1',
    date: 'Nov 24, 2025',
    title: 'Theme System Overhaul',
    categories: [
      {
        name: 'Themes',
        type: 'feature',
        icon: Palette,
        gradient: 'from-pink-500 to-purple-500',
        items: [
          'New light theme with improved contrast',
          'Custom accent color picker with presets',
          'Theme syncing across devices',
          'Improved syntax highlighting for 20+ languages',
        ],
      },
      {
        name: 'AI Improvements',
        type: 'feature',
        icon: Sparkles,
        gradient: 'from-purple-500 to-violet-500',
        items: [
          'Claude 3.5 Sonnet integration',
          'Improved code explanation quality',
          'Faster response times with streaming',
        ],
      },
      {
        name: 'Bug Fixes',
        type: 'bugfix',
        icon: Bug,
        gradient: 'from-red-500 to-rose-500',
        items: [
          'Fixed extension compatibility issues',
          'Resolved memory leaks in long sessions',
          'Fixed file watcher performance on Linux',
        ],
      },
    ],
  },
  {
    version: '2.0',
    date: 'Nov 10, 2025',
    title: 'Major Release - AI Agent Launch',
    highlight: 'Introducing AI Agent for complex tasks',
    categories: [
      {
        name: 'AI Agent',
        type: 'feature',
        icon: Sparkles,
        gradient: 'from-purple-500 to-violet-500',
        items: [
          'Introducing AI Agent for complex tasks',
          'Multi-step task execution with planning',
          'Codebase-aware suggestions',
          'Natural language to code conversion',
          'Automatic test generation',
        ],
      },
      {
        name: 'Performance',
        type: 'performance',
        icon: Zap,
        gradient: 'from-amber-500 to-orange-500',
        items: [
          'Completely rewritten editor core',
          '50% faster file loading',
          'Native ARM support for Apple Silicon',
        ],
      },
      {
        name: 'New Features',
        type: 'feature',
        icon: Terminal,
        gradient: 'from-purple-500 to-violet-500',
        items: [
          'Built-in database browser',
          'Docker integration with visual management',
          'Remote development support',
          'Collaborative editing (beta)',
        ],
      },
    ],
  },
  {
    version: '1.5',
    date: 'Oct 15, 2025',
    title: 'Extension Marketplace',
    categories: [
      {
        name: 'Extensions',
        type: 'feature',
        icon: Package,
        gradient: 'from-indigo-500 to-purple-500',
        items: [
          'New extension marketplace with search',
          'One-click extension installation',
          'Smart extension recommendations',
          'VS Code extension compatibility layer',
        ],
      },
      {
        name: 'Bug Fixes',
        type: 'bugfix',
        icon: Bug,
        gradient: 'from-red-500 to-rose-500',
        items: [
          'Fixed auto-save reliability issues',
          'Improved search performance',
          'Fixed keyboard shortcuts on Windows',
        ],
      },
    ],
  },
  {
    version: '1.4',
    date: 'Sep 28, 2025',
    title: 'Performance & Security',
    categories: [
      {
        name: 'Performance',
        type: 'performance',
        icon: Zap,
        gradient: 'from-amber-500 to-orange-500',
        items: [
          'Reduced CPU usage by 25%',
          'Optimized syntax highlighting',
          'Faster project switching',
        ],
      },
      {
        name: 'Security',
        type: 'security',
        icon: Shield,
        gradient: 'from-green-500 to-teal-500',
        items: [
          'SOC 2 Type II certification achieved',
          'Enhanced data encryption at rest',
          'New security dashboard for admins',
        ],
      },
    ],
  },
  {
    version: '1.3',
    date: 'Sep 10, 2025',
    title: 'Multi-Language Support',
    categories: [
      {
        name: 'Languages',
        type: 'feature',
        icon: Globe,
        gradient: 'from-purple-500 to-violet-500',
        items: [
          'Added support for 15 new programming languages',
          'Improved LSP integration',
          'Better IntelliSense for Python and Go',
        ],
      },
      {
        name: 'Bug Fixes',
        type: 'bugfix',
        icon: Bug,
        gradient: 'from-red-500 to-rose-500',
        items: [
          'Fixed auto-indent in JSX files',
          'Resolved syntax highlighting issues in Rust',
          'Fixed bracket matching in nested code',
        ],
      },
    ],
  },
];

const roadmapItems = [
  {
    title: 'Real-time Collaboration',
    description: 'Google Docs-style live coding',
    status: 'In Progress',
    statusColor: 'bg-blue-500',
  },
  {
    title: 'AI Code Review',
    description: 'Automated PR reviews',
    status: 'Coming Soon',
    statusColor: 'bg-purple-500',
  },
  {
    title: 'Mobile Companion',
    description: 'Review code on the go',
    status: 'Planned',
    statusColor: 'bg-amber-500',
  },
];

export function ChangelogPage() {
  const { t } = useTranslation();
  const [expandedVersions, setExpandedVersions] = useState<string[]>([changelog[0].version]);
  const [visibleCount, setVisibleCount] = useState(4);
  const [activeFilter, setActiveFilter] = useState<CategoryType | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);

  const filteredChangelog = changelog.filter((entry) => {
    const matchesFilter =
      activeFilter === 'all' ||
      entry.categories.some((cat) => cat.type === activeFilter);

    const matchesSearch =
      searchQuery === '' ||
      entry.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      entry.categories.some(
        (cat) =>
          cat.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          cat.items.some((item) => item.toLowerCase().includes(searchQuery.toLowerCase()))
      );

    return matchesFilter && matchesSearch;
  });

  const visibleChangelog = filteredChangelog.slice(0, visibleCount);
  const hasMore = visibleCount < filteredChangelog.length;

  const toggleVersion = (version: string) => {
    setExpandedVersions((prev) =>
      prev.includes(version)
        ? prev.filter((v) => v !== version)
        : [...prev, version]
    );
  };

  const handleSubscribe = (e: React.FormEvent) => {
    e.preventDefault();
    if (email) {
      setSubscribed(true);
      setEmail('');
    }
  };

  return (
    <>
      <SEO {...SEO_CONFIG.changelog} />
      <div className="min-h-screen relative overflow-hidden">
      <ChangelogBackground />
      {/* Hero */}
      <section className="section-lg relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-accent/5 via-transparent to-transparent" />

        <div className="container relative">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/10 border border-accent/20 text-accent text-sm mb-6">
              <Rocket size={16} />
              <span>{t('changelog.versionReleased', { version: changelog[0].version })}</span>
            </div>

            <h1 className="heading-xl mb-4">
              {t('changelog.title')}{' '}
              <span className="text-accent">{t('changelog.titleHighlight')}</span>
            </h1>
            <p className="text-text-secondary text-lg mb-8">
              {t('changelog.subtitle')}
            </p>

            {/* Search */}
            <div className="flex flex-col sm:flex-row gap-3 max-w-lg mx-auto">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-text-tertiary" />
                <input
                  type="text"
                  placeholder={t('changelog.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 rounded-xl bg-bg-card border border-border-default focus:border-accent focus:outline-none"
                />
              </div>
              <button className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-bg-card border border-border-default hover:border-accent transition-colors">
                <Rss className="w-5 h-5 text-accent" />
                <span className="sm:hidden">RSS</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Filter Tabs */}
      <section className="sticky top-16 z-20 bg-bg-base/95 backdrop-blur-sm border-b border-border-default py-4">
        <div className="container">
          <div className="flex gap-2 overflow-x-auto hide-scrollbar">
            {categoryFilterTypes.map((type) => (
              <button
                key={type}
                onClick={() => setActiveFilter(type)}
                className={cn(
                  'px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap',
                  activeFilter === type
                    ? 'bg-accent text-white'
                    : 'bg-bg-card text-text-secondary hover:text-text-primary hover:bg-bg-elevated'
                )}
              >
                {t(`changelog.filters.${type}`)}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Changelog Timeline */}
      <section className="section">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-4 md:left-6 top-0 bottom-0 w-px bg-border-default" />

              <div className="space-y-6">
                {visibleChangelog.map((entry, entryIndex) => (
                  <article key={entry.version} className="relative pl-12 md:pl-16">
                    {/* Version badge */}
                    <div className={cn(
                      'absolute left-0 w-8 h-8 md:w-12 md:h-12 rounded-xl flex items-center justify-center text-white font-bold text-sm md:text-base',
                      'bg-gradient-to-br',
                      entryIndex === 0 ? 'from-purple-500 to-violet-600' :
                      entryIndex === 1 ? 'from-purple-500 to-fuchsia-500' :
                      entryIndex === 2 ? 'from-amber-500 to-orange-500' :
                      'from-slate-500 to-zinc-600'
                    )}>
                      {entry.version}
                    </div>

                    {/* Card */}
                    <div className="card p-5 md:p-6">
                      {/* Header */}
                      <div className="flex flex-col md:flex-row md:items-center gap-2 mb-4">
                        <button
                          onClick={() => toggleVersion(entry.version)}
                          className="flex items-center gap-2 text-left group"
                        >
                          <h2 className="text-lg md:text-xl font-semibold group-hover:text-accent transition-colors">
                            {entry.title}
                          </h2>
                          <ChevronDown
                            size={20}
                            className={cn(
                              'text-text-tertiary transition-transform',
                              expandedVersions.includes(entry.version) && 'rotate-180'
                            )}
                          />
                        </button>

                        <div className="flex items-center gap-2 md:ml-auto">
                          {entry.isLatest && (
                            <span className="px-2 py-1 rounded-md text-xs font-medium bg-green-500/20 text-green-400">
                              {t('changelog.latest')}
                            </span>
                          )}
                          <span className="flex items-center gap-1 text-sm text-text-tertiary">
                            <Clock size={14} />
                            {entry.date}
                          </span>
                        </div>
                      </div>

                      {/* Highlight */}
                      {entry.highlight && expandedVersions.includes(entry.version) && (
                        <div className="flex items-center gap-2 mb-4 px-3 py-2 rounded-lg bg-accent/10 border border-accent/20 w-fit">
                          <Star size={14} className="text-accent" />
                          <span className="text-sm text-accent">{entry.highlight}</span>
                        </div>
                      )}

                      {/* Categories */}
                      {expandedVersions.includes(entry.version) && (
                        <div className="grid md:grid-cols-2 gap-4 mt-4">
                          {entry.categories.map((category) => (
                            <div
                              key={category.name}
                              className="p-4 rounded-xl bg-bg-elevated border border-border-default"
                            >
                              <div className="flex items-center gap-3 mb-3">
                                <div className={cn(
                                  'w-9 h-9 rounded-lg flex items-center justify-center bg-gradient-to-br',
                                  category.gradient
                                )}>
                                  <category.icon size={18} className="text-white" />
                                </div>
                                <div>
                                  <h3 className="font-medium">{category.name}</h3>
                                  <span className="text-xs text-text-tertiary">
                                    {category.items.length} {t('changelog.changes')}
                                  </span>
                                </div>
                              </div>
                              <ul className="space-y-2">
                                {category.items.map((item, index) => (
                                  <li
                                    key={index}
                                    className="flex items-start gap-2 text-sm text-text-secondary"
                                  >
                                    <CheckCircle2 size={14} className="text-green-500 flex-shrink-0 mt-0.5" />
                                    <span>{item}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </article>
                ))}
              </div>

              {/* Load more */}
              {hasMore && (
                <div className="text-center mt-8 pl-12 md:pl-16">
                  <button
                    onClick={() => setVisibleCount((prev) => Math.min(prev + 4, filteredChangelog.length))}
                    className="btn btn-secondary"
                  >
                    {t('changelog.loadOlder', { count: filteredChangelog.length - visibleCount })}
                    <ChevronDown size={18} />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Roadmap */}
      <section className="section bg-bg-surface">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="heading-lg mb-3">{t('changelog.roadmap.title')}</h2>
              <p className="text-text-secondary">
                {t('changelog.roadmap.subtitle')}
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              {roadmapItems.map((item, index) => (
                <div
                  key={index}
                  className="card p-5 hover:border-accent/30 transition-colors"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span className={cn('w-2 h-2 rounded-full', item.statusColor)} />
                    <span className="text-xs text-text-tertiary">{item.status}</span>
                  </div>
                  <h3 className="font-semibold mb-1">{item.title}</h3>
                  <p className="text-sm text-text-secondary">{item.description}</p>
                </div>
              ))}
            </div>

            <div className="text-center mt-6">
              <a
                href="https://github.com/ochtopus/roadmap"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-accent hover:underline text-sm"
              >
                {t('changelog.viewRoadmap')}
                <ChevronRight size={16} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Subscribe */}
      <section className="section">
        <div className="container">
          <div className="max-w-2xl mx-auto">
            <div className="card p-8 text-center bg-gradient-to-br from-accent/10 to-transparent border-accent/20">
              <Bell size={32} className="text-accent mx-auto mb-4" />
              <h2 className="heading-md mb-2">{t('changelog.subscribe.title')}</h2>
              <p className="text-text-secondary mb-6">
                {t('changelog.subscribe.description')}
              </p>

              {subscribed ? (
                <div className="flex items-center justify-center gap-2 text-green-400">
                  <CheckCircle2 size={20} />
                  <span>{t('changelog.subscribe.thanks')}</span>
                </div>
              ) : (
                <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                  <input
                    type="email"
                    placeholder={t('changelog.subscribe.placeholder')}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="flex-1 px-4 py-3 rounded-xl bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
                  />
                  <button type="submit" className="btn btn-primary">
                    {t('changelog.subscribe.button')}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
    </>
  );
}
